import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Alert,
  InputLabel,
  Collapse,
  Divider,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import { useEnumTranslation } from "../../utils/translateEnum";
import {
  useCompanies,
  useSearchCompanies,
  useSectorOptions,
  useCompanySizeOptions,
  useCompanyTypeOptions,
} from "../../hooks/useCompany";
import { zoullooColors } from "../../theme";
import { TablePagination, SortOption, SortConfig } from "../../components/common/pagination";
import { CompaniesSortField } from "../../models/enums/CompaniesSortField";
import { useSortState } from "../../hooks/useSortState";

const CompanyList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateByEnvironment } = useEnvironmentNavigation();

  // State for search and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchError, setSearchError] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Sort state (shared hook owns the lowercase<->Asc/Desc conversion)
  const { sortField, sortDirection, currentSort, applySort } = useSortState();

  // Filter states
  const [selectedSector, setSelectedSector] = useState<number | undefined>();
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedType, setSelectedType] = useState("");

  // Get filter options
  const { data: sectorOptions } = useSectorOptions();
  const { data: companySizeOptions } = useCompanySizeOptions();
  const { data: companyTypeOptions } = useCompanyTypeOptions();

  // Get translation functions for enum values
  const { translateCompanySize, translateCompanyType } =
    useEnumTranslation();

  // Use the original companies hook for initial loading (before search)
  const {
    data: initialData,
    isLoading: isLoadingInitial,
    error: initialError,
  } = useCompanies(page, pageSize, sortField, sortDirection);

  // Use the search hook from hooks directory
  const {
    data: searchData,
    isLoading: isSearching,
    error: searchApiError,
    isSearched,
    searchCompanies,
    resetSearch,
  } = useSearchCompanies();

  // Combined data and loading state
  const data = isSearched ? searchData : initialData;
  const isLoading = isLoadingInitial || isSearching;
  const error = isSearched ? searchApiError : initialError;

  // Determine if search is possible
  const canSearch = useMemo(() => {
    return (
      searchTerm.trim().length >= 2 ||
      !!selectedSector ||
      !!selectedSize ||
      !!selectedType
    );
  }, [searchTerm, selectedSector, selectedSize, selectedType]);

  // Effect to reset page when search term changes significantly
  useEffect(() => {
    if (
      isSearched &&
      searchTerm.trim() === "" &&
      !selectedSector &&
      !selectedSize &&
      !selectedType
    ) {
      resetSearch();
    }
  }, [
    searchTerm,
    isSearched,
    resetSearch,
    selectedSector,
    selectedSize,
    selectedType,
  ]);

  const companies = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 0;

  const handleCreateCompany = () => {
    navigate("/companies/create");
  };

  const handleViewCompany = (id: number) => {
    navigateByEnvironment(`/companies/${id}`);
  };

  const handleEditCompany = (id: number) => {
    navigateByEnvironment(`/companies/${id}/edit`);
  };

  const handleSearch = () => {

    // Clear any previous error
    setSearchError("");

    // Do nothing if there are no search criteria
    if (!canSearch) {
      setSearchError(t("company.noSearchCriteria"));
      return;
    }

    // Reset page to 1 when performing a new search
    setPage(1);
    searchCompanies(
      searchTerm,
      1,
      pageSize,
      selectedSector,
      selectedSize,
      selectedType,
      sortField,
      sortDirection
    );
  };
  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedSector(undefined);
    setSelectedSize("");
    setSelectedType("");
    setSearchError("");
    setPage(1);
    resetSearch();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSortChange = (sort: SortConfig) => {
    applySort(sort);
    setPage(1);
  };

  // Re-run the active search whenever paging or sorting changes. Driving this
  // from an effect (instead of fetching inside each handler) collapses the
  // multiple state updates of one interaction — and TablePagination's extra
  // onPageChange(1) — into a single request with the final values, avoiding
  // the duplicate/stale fetch race.
  useEffect(() => {
    if (!isSearched) return;
    searchCompanies(
      searchTerm,
      page,
      pageSize,
      selectedSector,
      selectedSize,
      selectedType,
      sortField,
      sortDirection
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortField, sortDirection]);

  const sortOptions: SortOption[] = [
    { label: t("company.sort.legalName"), value: CompaniesSortField.LegalName },
    { label: t("company.sort.tradingName"), value: CompaniesSortField.TradingName },
    { label: t("company.sort.sector"), value: CompaniesSortField.Sector },
    { label: t("company.sort.size"), value: CompaniesSortField.Size },
    { label: t("company.sort.type"), value: CompaniesSortField.Type },
    { label: t("company.sort.createdAt"), value: CompaniesSortField.CreatedAt },
  ];

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && canSearch) {
      handleSearch();
    }
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedSector) count++;
    if (selectedSize) count++;
    if (selectedType) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  if (isLoading && !isSearched) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !isSearching) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t("common.errorLoading")}: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box px={6} py={4}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h2">{t("company.companyList")}</Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            {t("client.showing")} {totalCount}{" "}
            {t("menu.companies").toLowerCase()}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCompany}
          sx={{
            backgroundColor: zoullooColors.secondary.main,
            color: zoullooColors.secondary.contrastText,
            "&:hover": {
              backgroundColor: zoullooColors.secondary.dark,
            },
          }}
        >
          {t("company.createNew")}
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Champ de recherche qui prend l'espace disponible */}
          <Box sx={{ flex: "1 1 auto" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("company.searchCompanies")}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSearchError("");
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { height: "40px" },
              }}
              error={!!searchError}
              helperText={searchError}
              size="small"
            />
          </Box>

          {/* Groupe de boutons alignés à droite */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {/* Bouton Filtres */}
            <Button
              variant="outlined"
              onClick={handleToggleFilters}
              startIcon={
                activeFiltersCount > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: zoullooColors.primary.main,
                      color: zoullooColors.text.white,
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                    }}
                  >
                    {activeFiltersCount}
                  </Box>
                ) : null
              }
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                height: "40px",
                color: zoullooColors.secondary.main,
                borderColor: zoullooColors.secondary.main,
                ...(showFilters && {
                  backgroundColor: zoullooColors.grey[100],
                  borderColor: zoullooColors.secondary.main,
                  "&:hover": {
                    backgroundColor: zoullooColors.grey[200],
                    borderColor: zoullooColors.secondary.dark,
                  },
                }),
                "&:hover": {
                  borderColor: zoullooColors.secondary.main,
                }
              }}
            >
              {t("common.filters")}
            </Button>

            {/* Bouton de recherche */}
            <Tooltip
              title={!canSearch ? t("company.enterSearchOrSelectFilter") : ""}
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  disabled={isSearching || !canSearch}
                  sx={{
                    height: "40px",
                    borderColor: zoullooColors.secondary.main,
                    color: zoullooColors.secondary.main,
                    backgroundColor: zoullooColors.background.default,
                    "&:hover": {
                      borderColor: zoullooColors.secondary.dark,
                      backgroundColor: zoullooColors.secondary.alpha
                    },
                    opacity: !canSearch && !isSearching ? 0.7 : 1,
                  }}
                >
                  {isSearching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("common.search")
                  )}
                </Button>
              </span>
            </Tooltip>

            {/* Bouton pour effacer la recherche */}
            {(isSearched || activeFiltersCount > 0) && (
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                sx={{
                  height: "40px",
                  borderColor: zoullooColors.secondary.main,
                  color: zoullooColors.secondary.main,
                  "&:hover": {
                    borderColor: zoullooColors.secondary.dark,
                    backgroundColor: zoullooColors.secondary.alpha
                  }
                }}
              >
                {t("common.clear")}
              </Button>
            )}
          </Box>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Box mt={2}>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ "&.Mui-focused": { color: zoullooColors.secondary.main } }}>{t("company.industry")}</InputLabel>
                  <Select
                    value={selectedSector}
                    onChange={(e) => setSelectedSector(e.target.value ? Number(e.target.value) : undefined)}
                    label={t("company.industry")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.border.main
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      }
                    }}
                  >
                    <MenuItem value="">{t("common.all")}</MenuItem>
                    {sectorOptions?.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ "&.Mui-focused": { color: zoullooColors.secondary.main } }}>{t("company.size")}</InputLabel>
                  <Select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    label={t("company.size")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.border.main
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      }
                    }}
                  >
                    <MenuItem value="">{t("common.all")}</MenuItem>
                    {companySizeOptions?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {translateCompanySize(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ "&.Mui-focused": { color: zoullooColors.secondary.main } }}>{t("company.type")}</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    label={t("company.type")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.border.main
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: zoullooColors.secondary.main
                      }
                    }}
                  >
                    <MenuItem value="">{t("common.all")}</MenuItem>
                    {companyTypeOptions?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {translateCompanyType(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Active filters */}
      <Box mb={3}>
        {/* Active filters display */}
        <Box>
          {activeFiltersCount > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t("common.activeFilters")}:
              </Typography>

              {selectedSector && (
                <Box
                  component="span"
                  sx={{
                    p: "4px 8px",
                    bgcolor: zoullooColors.secondary.main,
                    color: zoullooColors.text.white,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("company.industry")}:{" "}
                  {sectorOptions?.find(s => s.id === selectedSector)?.label || selectedSector}
                </Box>
              )}

              {selectedSize && (
                <Box
                  component="span"
                  sx={{
                    p: "4px 8px",
                    bgcolor: zoullooColors.secondary.main,
                    color: zoullooColors.text.white,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("company.size")}: {translateCompanySize(selectedSize)}
                </Box>
              )}

              {selectedType && (
                <Box
                  component="span"
                  sx={{
                    p: "4px 8px",
                    bgcolor: zoullooColors.secondary.main,
                    color: zoullooColors.text.white,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("company.type")}: {translateCompanyType(selectedType)}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {isSearching && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {isSearched && !isSearching && companies.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("company.noSearchResults", { searchTerm })}
        </Alert>
      )}

      {!isSearching && (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("company.legalName")}</TableCell>
                <TableCell>{t("company.tradingName")}</TableCell>
                <TableCell>{t("company.industry")}</TableCell>
                <TableCell>{t("company.size")}</TableCell>
                <TableCell>{t("company.type")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies && companies.length > 0 ? (
                companies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>{company.legalName}</TableCell>
                    <TableCell>{company.tradingName || "-"}</TableCell>
                    <TableCell>
                      {company.sector
                        ? company.sector.label
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {company.companySize
                        ? translateCompanySize(company.companySize)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {company.companyType
                        ? translateCompanyType(company.companyType)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewCompany(company.id)}
                        color="secondary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCompany(company.id)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {isSearched
                      ? t("company.noSearchResults", { searchTerm })
                      : t("company.noCompaniesFound")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination with sort */}
      {totalPages > 0 && !isSearching && (
        <TablePagination
          currentPage={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 20, 50]}
          sortOptions={sortOptions}
          currentSort={currentSort}
          onSortChange={handleSortChange}
          disabled={false}
          sticky={false}
          mx={0}
        />
      )}
    </Box>
  );
};

export default CompanyList;
